import React from "react";
import { Link } from "../router";
import PageHero from "../components/PageHero";
import AssetVisual from "../components/AssetVisual";

const SECTORS = [
  { c: "Apartments", line: "Turnkey, ultra-modern, concierge-grade living — handed over move-in ready." },
  { c: "Villas", line: "Standalone craftsmanship: private land, bespoke design, full structural control." },
  { c: "Houses", line: "Independent homes on clean, marketable plots — restrained and ready." },
  { c: "Plots", line: "Gated, build-ready parcels with utilities laid and title reconciled." },
  { c: "Land", line: "Converted agricultural and commercial belts in appreciating corridors." },
];

const PROTOCOL = [
  ["01", "Seller paperwork intake", "We collect the full document set — sale deed, lineage, approvals, tax receipts — before anything is shown to a buyer."],
  ["02", "Aditi Legal Shield review", "Our in-house counsel runs an independent secondary check: encumbrance, litigation history, title lineage, and statutory approvals."],
  ["03", "Physical & status verification", "Feet on the ground. We confirm the asset exists as described, is unoccupied/undisputed, and is genuinely available to transact."],
  ["04", "Live & Available stamp", "Only cleared assets are published — each with a verified timestamp and the Certified Title Check badge."],
  ["05", "Continuous re-verification", "Listings are re-checked on a fixed cadence. Go cold, contested, or sold? It's removed the same day."],
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="The House · Aditi Realty"
        title="We sell certainty"
        italic="first."
        sub="Aditi Realty exists because buying property in India is too often an act of faith. We replaced faith with a protocol — double verification, in-house legal vetting, and end-to-end design-and-build under one accountable roof."
        tone={["#0B0B0C", "#211a10"]}
      />

      {/* Brand narrative */}
      <section className="section">
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "0.9fr 1.1fr", gap: "clamp(32px,6vw,90px)", alignItems: "start" }}>
            <div className="reveal">
              <span className="eyebrow">Our standard</span>
              <h2 className="h-xl" style={{ marginTop: 16 }}>Transparency<br />is the product.</h2>
            </div>
            <div className="reveal stack-sm">
              <p className="lead">
                Most portals are inventory dumps — endless listings, stale prices,
                and titles nobody actually checked. We took the opposite position.
              </p>
              <p className="muted">
                Every asset on Aditi Realty is curated, not crowdsourced. Before a
                property earns a place here, it passes a secondary verification run
                by our own legal team — independent of whatever the seller claims.
                We confirm the title is clean, the asset is real, and it is
                genuinely available to buy today. If it can't clear that bar, it
                doesn't list.
              </p>
              <p className="muted">
                And because we also design and build, our judgement of an asset
                isn't theoretical. We know what a plot can become, what a structure
                will cost to finish, and where value is genuinely created. That is
                the difference between a broker and a house.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Asset class highlight */}
      <section className="section dark" style={{ paddingTop: "clamp(56px,8vw,104px)" }}>
        <div className="shell">
          <div className="reveal" style={{ maxWidth: 620, marginBottom: 48 }}>
            <span className="eyebrow on-dark">How we treat each sector</span>
            <h2 className="h-xl" style={{ marginTop: 16 }}>From raw land to<br />turnkey luxury.</h2>
          </div>
          <div style={{ borderTop: "1px solid var(--obsidian-line)" }}>
            {SECTORS.map((s, i) => (
              <Link
                key={s.c}
                to={`/properties?class=${s.c}`}
                className="reveal sector-row"
                style={{
                  display: "grid", gridTemplateColumns: "80px 1fr 2fr auto", gap: 24, alignItems: "center",
                  padding: "26px 0", borderBottom: "1px solid var(--obsidian-line)",
                }}
              >
                <span style={{ fontFamily: "var(--serif)", fontSize: "1.4rem", color: "var(--champagne)" }}>0{i + 1}</span>
                <span style={{ fontFamily: "var(--serif)", fontSize: "1.5rem" }}>{s.c}</span>
                <span className="muted">{s.line}</span>
                <span className="gold-text" style={{ justifySelf: "end" }}>→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* The Aditi Guarantee */}
      <section className="section">
        <div className="shell">
          <div className="reveal" style={{ maxWidth: 640, marginBottom: 52 }}>
            <span className="eyebrow gold">The Aditi Guarantee</span>
            <h2 className="h-xl" style={{ marginTop: 16 }}>How we eliminate<br />dead inventory — entirely.</h2>
            <p className="lead" style={{ marginTop: 20 }}>
              "Dead inventory" — stale, disputed, or quietly-sold listings — is the
              single biggest waste of a serious buyer's time. Here is the exact
              protocol that keeps it off our platform.
            </p>
          </div>

          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {PROTOCOL.map(([no, t, body]) => (
              <div key={no} className="reveal" style={{ borderTop: "1px solid var(--alabaster-dim)", paddingTop: 22 }}>
                <div className="spaced">
                  <span style={{ fontFamily: "var(--serif)", fontSize: "2rem", color: "var(--champagne)" }}>{no}</span>
                </div>
                <h3 className="h-md" style={{ margin: "12px 0 10px" }}>{t}</h3>
                <p className="muted" style={{ fontSize: "0.95rem" }}>{body}</p>
              </div>
            ))}
            <div className="reveal" style={{ borderRadius: 6, overflow: "hidden", minHeight: 220 }}>
              <AssetVisual tone={["#16130e", "#322a18"]} accent="#D4AF37" label="◈ Certified Title Check" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm">
        <div className="shell center reveal">
          <h2 className="h-lg">Hold one standard. Everywhere.</h2>
          <p className="lead maxw-680" style={{ margin: "14px auto 30px" }}>
            Whether you're acquiring, selling, building, or investing — the
            verification never changes.
          </p>
          <div className="flexrow" style={{ justifyContent: "center" }}>
            <Link to="/properties" className="btn btn-ink">Browse the portfolio <span className="arr">→</span></Link>
            <Link to="/contact" className="btn btn-ghost">Talk to the house</Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 820px) {
          .section .grid[style*="0.9fr"] { grid-template-columns: 1fr !important; }
          .section .grid[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
          .sector-row { grid-template-columns: 50px 1fr !important; grid-template-areas: "no name" "body body"; row-gap: 8px !important; }
          .sector-row > span:nth-child(3) { grid-column: 1 / -1; }
          .sector-row > span:nth-child(4) { display: none; }
        }
      `}</style>
    </>
  );
}
